import { ref, watch, type Ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'

import { materialsApi, type Material } from '@/api'
import request from '@/api/request'

import type {
  AssetTab,
  ContentLibraryEntry,
  ContentLibraryFilters,
  ContentLibraryResponse,
  MaterialLibraryEntry,
  MaterialLibraryFilters,
  MaterialLibraryResponse,
} from './types'

interface UseKnowledgeBaseLibrariesOptions {
  activeTab: Ref<AssetTab>
  contentFilters: ContentLibraryFilters
  imageFilters: MaterialLibraryFilters
  linkedProjectId: Ref<number | null>
}

export function useKnowledgeBaseLibraries(options: UseKnowledgeBaseLibrariesOptions) {
  const loadingContent = ref(false)
  const loadingMaterials = ref(false)
  const deletingContentId = ref<string | null>(null)
  const deletingMaterialId = ref<number | null>(null)

  const contentEntries = ref<ContentLibraryEntry[]>([])
  const materialEntries = ref<MaterialLibraryEntry[]>([])

  async function loadContentEntries() {
    loadingContent.value = true
    try {
      const response = await request.get<ContentLibraryResponse>('/knowledge/content-library', {
        params: {
          query: options.contentFilters.query || undefined,
          content_type: options.contentFilters.contentType || undefined,
          limit: 120,
          offset: 0,
        },
      })
      contentEntries.value = response.items || []
    } catch (error: any) {
      contentEntries.value = []
      ElMessage.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '系统内容素材加载失败')
    } finally {
      loadingContent.value = false
    }
  }

  async function handleDeleteContentEntry(entry: ContentLibraryEntry) {
    if (!entry?.id || deletingContentId.value) {
      return
    }

    try {
      await ElMessageBox.confirm('确定删除这条系统内容素材吗？删除后内容驱动项目将不能继续复用它。', '删除内容素材', {
        type: 'warning',
        confirmButtonText: '删除',
        cancelButtonText: '取消',
      })
    } catch {
      return
    }

    deletingContentId.value = entry.id
    try {
      await request.delete(`/knowledge/content-library/${encodeURIComponent(entry.id)}`)
      contentEntries.value = contentEntries.value.filter((item) => item.id !== entry.id)
      ElMessage.success('内容素材已删除')
    } catch (error: any) {
      ElMessage.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || '删除内容素材失败')
    } finally {
      deletingContentId.value = null
    }
  }

  async function loadMaterialEntries() {
    loadingMaterials.value = true
    try {
      const isAudioTab = options.activeTab.value === 'audio'
      const assetKind = options.activeTab.value === 'video' ? 'video' : 'image'
      const params = {
        asset_kind: assetKind,
        project_id: options.imageFilters.scope === 'current_project' ? options.linkedProjectId.value || undefined : undefined,
        query: options.imageFilters.query || undefined,
        limit: 120,
        offset: 0,
      }

      if (!isAudioTab) {
        try {
          const response = await request.get<MaterialLibraryResponse>('/knowledge/materials', {
            params,
            suppressErrorMessage: true,
          } as any)
          materialEntries.value = response.items || []
          return
        } catch (error: any) {
          if (error?.response?.status !== 404) {
            throw error
          }
        }
      }

      const fallback = await materialsApi.list({
        project_id: params.project_id,
        material_type: options.activeTab.value === 'video'
          ? 'video_clip'
          : options.activeTab.value === 'audio'
            ? 'audio_file'
            : undefined,
        page: 1,
        page_size: 120,
      })
      const normalizedQuery = String(options.imageFilters.query || '').trim().toLowerCase()

      materialEntries.value = (fallback.items || [])
        .filter((item) => Boolean(item.file_url))
        .filter((item) => {
          if (options.activeTab.value === 'audio') {
            return item.material_type === 'audio_file' || item.material_type === 'music_file'
          }
          if (options.activeTab.value === 'video') {
            return item.material_type === 'video_clip'
          }
          return item.material_type !== 'video_clip' && item.material_type !== 'audio_file' && item.material_type !== 'music_file'
        })
        .filter((item) => {
          const haystack = [
            item.name || '',
            item.description || '',
            item.metadata?.description || '',
            String(item.project_id || ''),
          ].join('\n').toLowerCase()
          return !normalizedQuery || haystack.includes(normalizedQuery)
        })
        .map((item: Material) => ({
          id: item.id,
          project_id: item.project_id,
          project_title: options.linkedProjectId.value && item.project_id === options.linkedProjectId.value ? '当前项目' : `项目 ${item.project_id}`,
          name: item.name,
          description: item.description || item.metadata?.description || null,
          image_url: String(item.file_url || ''),
          source: String(item.source || 'uploaded_user'),
          material_type: item.material_type,
          duration: item.duration,
          created_at: item.created_at,
          updated_at: item.updated_at,
        }))
    } catch (error: any) {
      materialEntries.value = []
      const assetLabel = options.activeTab.value === 'video' ? '视频' : options.activeTab.value === 'audio' ? '音频' : '图片'
      ElMessage.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || `${assetLabel}素材加载失败`)
    } finally {
      loadingMaterials.value = false
    }
  }

  async function handleDeleteMaterial(entry: MaterialLibraryEntry) {
    if (!entry?.id || deletingMaterialId.value) {
      return
    }

    try {
      const assetLabel = entry.material_type === 'video_clip' ? '视频' : (entry.material_type === 'audio_file' || entry.material_type === 'music_file') ? '音频' : '图片'
      await ElMessageBox.confirm(`确定删除${assetLabel}“${entry.name}”吗？删除后会从当前项目素材中移除。`, `删除${assetLabel}`, {
        type: 'warning',
        confirmButtonText: '删除',
        cancelButtonText: '取消',
      })
    } catch {
      return
    }

    deletingMaterialId.value = entry.id
    try {
      const assetLabel = entry.material_type === 'video_clip' ? '视频' : (entry.material_type === 'audio_file' || entry.material_type === 'music_file') ? '音频' : '图片'
      await materialsApi.delete(entry.id)
      materialEntries.value = materialEntries.value.filter((item) => item.id !== entry.id)
      ElMessage.success(`${assetLabel}已删除`)
    } catch (error: any) {
      const assetLabel = entry.material_type === 'video_clip' ? '视频' : (entry.material_type === 'audio_file' || entry.material_type === 'music_file') ? '音频' : '图片'
      ElMessage.error(error?.response?.data?.detail || error?.response?.data?.message || error?.message || `删除${assetLabel}失败`)
    } finally {
      deletingMaterialId.value = null
    }
  }

  watch(
    () => options.activeTab.value,
    (tab) => {
      if (tab === 'content' && !contentEntries.value.length) {
        void loadContentEntries()
      }
      if (tab === 'image' || tab === 'video' || tab === 'audio') {
        void loadMaterialEntries()
      }
    }
  )

  return {
    contentEntries,
    deletingContentId,
    deletingMaterialId,
    handleDeleteContentEntry,
    handleDeleteMaterial,
    loadContentEntries,
    loadMaterialEntries,
    loadingContent,
    loadingMaterials,
    materialEntries,
  }
}